import type { MfgMaterial, MfgRecipeKind, MfgRecipeSummary } from "@/lib/manufacturing/types";
import type { MfgRecipeBulkCreateResponse } from "@/lib/manufacturing/types/recipeImport";
import { ManufacturingModelsBulkImportModal } from "./ManufacturingModelsBulkImportModal";
import { ManufacturingModelsBomSandboxModal } from "./ManufacturingModelsBomSandboxModal";
import { ManufacturingModelsDictateModal } from "./ManufacturingModelsDictateModal";
import { ManufacturingModelDeleteConfirm } from "./ManufacturingModelDeleteConfirm";
import { ManufacturingModelPermanentDeleteConfirm } from "./ManufacturingModelPermanentDeleteConfirm";

type BulkSummary = MfgRecipeBulkCreateResponse["summary"];

/** Overlay modals owned by ManufacturingModelsPanel (keeps the panel under size cap). */
export function ManufacturingModelsPanelModals({
  showBulkImport,
  showBomSandbox,
  bomSandboxGroupId,
  materials,
  recipes,
  onCloseBulkImport,
  onBulkImported,
  onPickRecipe,
  onCloseBomSandbox,
  onBomSandboxCommitted,
  dictateTranscript,
  orgSlotLabels,
  dictateDefaultKind,
  onCloseDictate,
  onDictateSaved,
  deleteConfirmOpen,
  selectedRecipe,
  deleteBusy,
  deleteError,
  onCancelDeactivate,
  onConfirmDeactivate,
  permanentDeleteOpen,
  permanentDeleteTarget,
  permanentDeleteBlockers,
  onCancelPermanentDelete,
  onConfirmPermanentDelete,
}: {
  showBulkImport: boolean;
  showBomSandbox: boolean;
  bomSandboxGroupId?: string | null;
  materials: MfgMaterial[];
  recipes: MfgRecipeSummary[];
  onCloseBulkImport: () => void;
  onBulkImported: (summary: BulkSummary) => void;
  onPickRecipe: (id: string) => void;
  onCloseBomSandbox: () => void;
  onBomSandboxCommitted: (recipeId: string) => void;
  dictateTranscript: string | null;
  orgSlotLabels: readonly string[];
  dictateDefaultKind: MfgRecipeKind;
  onCloseDictate: () => void;
  onDictateSaved: (summary: BulkSummary) => void;
  deleteConfirmOpen: boolean;
  selectedRecipe: MfgRecipeSummary | null;
  deleteBusy: boolean;
  deleteError: string | null;
  onCancelDeactivate: () => void;
  onConfirmDeactivate: () => void;
  permanentDeleteOpen: boolean;
  permanentDeleteTarget: MfgRecipeSummary | null;
  permanentDeleteBlockers: ReadonlyArray<{ code: string; message: string }>;
  onCancelPermanentDelete: () => void;
  onConfirmPermanentDelete: () => void;
}) {
  return (
    <>
      {showBulkImport ? (
        <ManufacturingModelsBulkImportModal
          existingMaterials={materials}
          existingRecipes={recipes}
          onClose={onCloseBulkImport}
          onImported={onBulkImported}
          onPickRecipe={onPickRecipe}
        />
      ) : null}

      {showBomSandbox ? (
        <ManufacturingModelsBomSandboxModal
          onClose={onCloseBomSandbox}
          onCommitted={onBomSandboxCommitted}
          initialRecipeGroupId={bomSandboxGroupId}
        />
      ) : null}

      {dictateTranscript !== null ? (
        <ManufacturingModelsDictateModal
          transcript={dictateTranscript}
          materials={materials}
          orgSlotLabels={orgSlotLabels}
          defaultKind={dictateDefaultKind}
          onClose={onCloseDictate}
          onSaved={onDictateSaved}
        />
      ) : null}

      <ManufacturingModelDeleteConfirm
        open={deleteConfirmOpen}
        recipe={selectedRecipe}
        busy={deleteBusy}
        error={deleteError}
        onCancel={onCancelDeactivate}
        onConfirm={onConfirmDeactivate}
      />

      <ManufacturingModelPermanentDeleteConfirm
        open={permanentDeleteOpen}
        recipe={permanentDeleteTarget}
        busy={deleteBusy}
        error={deleteError}
        blockers={permanentDeleteBlockers}
        onCancel={onCancelPermanentDelete}
        onConfirm={onConfirmPermanentDelete}
      />
    </>
  );
}
